import {
  Briefcase,
  Coins,
  CreditCard,
  FileText,
  LayoutTemplate,
  LifeBuoy,
  Megaphone,
  Newspaper,
  Sparkles,
  Receipt,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { routes } from '@/shared/constants'
import type { Permission } from '@/features/auth'

export interface NavLeaf {
  kind: 'leaf'
  id: string
  to: string
  label: string
  icon: LucideIcon
  comingSoon?: boolean
  /** Pasa a `<NavLink end>` para match exacto (útil en la raíz `/app`). */
  end?: boolean
  /** Permiso requerido para mostrar el item. Si no se define, todos lo ven. */
  permission?: Permission
  /**
   * El item se gatea por el ALCANCE de tickets (query orgTicketScope, goal
   * portal-tickets-alcance-por-membresia) en vez de por permiso de rol.
   */
  requiresTicketScope?: boolean
  /**
   * El item solo se muestra si el usuario tiene al menos un negocio
   * (`useHasBusinesses`). Sin negocios estas vistas devuelven 403 (Facturación,
   * Créditos y recargas), así que se ocultan del menú.
   */
  requiresBusiness?: boolean
}

export interface NavGroup {
  kind: 'group'
  id: string
  label: string
  icon: LucideIcon
  /** Prefijo de ruta que activa el auto-expand del grupo. */
  matchPath: string
  children: NavLeaf[]
}

export type NavEntry = NavLeaf | NavGroup

export interface NavSectionData {
  id: string
  title: string
  items: NavEntry[]
}

/**
 * Estructura de navegación del sidebar. Para agregar un nuevo item o sub-grupo,
 * basta con extender este array — los componentes hacen `switch (item.kind)`
 * con narrowing exhaustivo.
 */
export const navigation: NavSectionData[] = [
  {
    id: 'main',
    title: 'Principal',
    items: [
      {
        // Fusiona "Mi organización" + negocios: la vista muestra los datos de la
        // organización arriba y el listado de negocios debajo.
        kind: 'leaf',
        id: 'businesses',
        to: routes.app.businesses.list,
        label: 'Mis negocios',
        icon: Briefcase,
        permission: 'businesses:read',
      },
      {
        kind: 'leaf',
        id: 'services',
        to: routes.app.services,
        label: 'Servicios',
        icon: Wrench,
      },
      {
        // Lo nuevo de cada producto (goal novedades-del-portal 2). Sin
        // `permission` ni `requiresBusiness`: lo ve todo el mundo, igual que
        // las notas de versión públicas de las que sale.
        kind: 'leaf',
        id: 'novedades',
        to: routes.app.novedades,
        label: 'Novedades',
        icon: Sparkles,
      },
      {
        kind: 'leaf',
        id: 'billing',
        to: routes.app.billing.list,
        label: 'Facturación',
        icon: Receipt,
        permission: 'billing:read',
        requiresBusiness: true,
      },
      {
        kind: 'leaf',
        id: 'credits',
        to: routes.app.credits,
        label: 'Créditos y recargas',
        icon: Coins,
        // Créditos sigue siendo exclusivo del dueño (goal portal-roles-1).
        permission: 'credits:read',
        requiresBusiness: true,
      },
      {
        // Tickets de soporte por ALCANCE (goal portal-tickets-alcance-por-
        // membresia): cualquier usuario con organización o negocios lo ve.
        // El gate duro es la resolución de alcance en tickets-server; esto
        // solo oculta el item (SidebarSection consulta orgTicketScope).
        kind: 'leaf',
        id: 'tickets',
        to: routes.app.tickets.list,
        label: 'Tickets',
        icon: LifeBuoy,
        requiresTicketScope: true,
      },
      {
        // Equipo de la organización (goal portal-roles-2): solo el dueño
        // (users:list es OWNER-only en el mapa rol→permisos).
        kind: 'leaf',
        id: 'team',
        to: routes.app.team,
        label: 'Equipo',
        icon: Users,
        permission: 'users:list',
      },
    ],
  },
  {
    // Sólo visible para PORTAL_ADMIN (staff TecoPOS). Si el usuario no tiene
    // `landing:edit`, la sección entera se oculta (SidebarSection filtra).
    id: 'admin',
    title: 'Administración',
    items: [
      {
        kind: 'leaf',
        id: 'admin-landing',
        to: routes.app.admin.landing,
        label: 'Contenido del landing',
        icon: LayoutTemplate,
        permission: 'landing:edit',
      },
      {
        kind: 'leaf',
        id: 'admin-legal',
        to: routes.app.admin.legal,
        label: 'Páginas legales',
        icon: FileText,
        permission: 'landing:edit',
      },
      {
        kind: 'leaf',
        id: 'admin-news',
        to: routes.app.admin.news,
        label: 'Lo último',
        icon: Newspaper,
        permission: 'landing:edit',
      },
      {
        kind: 'leaf',
        id: 'admin-commercials',
        to: routes.app.admin.commercials,
        label: 'Comerciales',
        icon: Users,
        permission: 'landing:edit',
      },
      {
        kind: 'leaf',
        id: 'admin-difusion',
        to: routes.app.admin.difusion,
        label: 'Difusión de novedades',
        icon: Megaphone,
        permission: 'landing:edit',
      },
      {
        kind: 'leaf',
        id: 'admin-payment-gateways',
        to: routes.app.admin.paymentGateways,
        label: 'Pasarelas de pago',
        icon: CreditCard,
        permission: 'payments:manage',
      },
      {
        // Gestión de usuarios: es administrativa (staff), no para el Lead.
        kind: 'leaf',
        id: 'users',
        to: routes.app.users,
        label: 'Usuarios',
        icon: Users,
        permission: 'admins:manage',
      },
    ],
  },
]
